import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, finalize, take } from 'rxjs';

// Serviços
import { ProfileService } from '../../services/profile/profile.service';
import { LoadingService } from '../../components/loading/loading.service';
import { ToastService } from '../../services/toast/toast.service';
import { AuthService } from 'src/app/modules/services/auth/auth.service';
import { UserService } from 'src/app/modules/services/user/user.service';
import { PageSizeService } from 'src/app/modules/services/page-size/page-size.service';
import { PageStateService } from 'src/app/modules/services/page-state/page-state.service';

// Componentes
import { ModalComponent } from '../../components/modal/modal.component';

@Component({
  selector: 'app-profile-list',
  templateUrl: './profile-list.component.html',
  styleUrls: ['./profile-list.component.scss'],
})
export class ProfileListComponent implements OnInit, OnDestroy {
  // Configurações da Tabela
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  
  columnConfig = [
    { columnDef: 'nome', header: 'Perfil', property: 'nome', customTd: true },
    { columnDef: 'criadoEm', header: 'Data de cadastro', property: 'criadoEm', customTd: true },
    { columnDef: 'status', header: 'Status', property: 'status', customTd: true },
    { columnDef: 'actions', header: 'Ações', custom: true }
  ];

  arrayStatus: any = [
    { label: "Ativo", property: "true", selected: false },
    { label: "Inativo", property: "false", selected: false },
  ];

  // Controle de Paginação e Ordenação
  pageSize: number = 10;
  pageIndex: number = 0;
  totalElements: number = 0;
  totalPages: number = 1;
  sortState: any = { sortProperty: 'criadoEm', sortDirection: 'desc' };

  // Filtros
  filterTextValue: string = '';
  statusFilter: string = '';

  // Permissões e UI
  buttonInclusaoDisabled: boolean = false;
  buttonExclusaoDisabled: boolean = false;
  buttonEdicaoDisabled: boolean = false;
  currentRoute: string = '';
  disabledTooltipMessage: string = 'Ação não permitida';
  
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private profileService: ProfileService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    public dialog: MatDialog,
    private authService: AuthService,
    private userService: UserService,
    private pageSizeService: PageSizeService,
    private pageStateService: PageStateService
  ) {
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this.displayedColumns = this.columnConfig.map(col => col.columnDef);
    
    const savedPageIndex = this.pageStateService.getPageState(this.router.url);
    if (savedPageIndex) {
      this.pageIndex = savedPageIndex;
    }

    this.loadUserInfo();
    this.onGetUserProfiles(this.pageIndex);
  }

  /**
   * Busca principal de dados
   */
  onGetUserProfiles(pageIndex: number = 0) {
    this.loadingService.showLoading();

    this.profileService.getProfiles(
      this.pageSize,
      pageIndex,
      this.sortState.sortDirection,
      this.sortState.sortProperty,
      this.filterTextValue, // emailFilter
      this.filterTextValue, // nameFilter
      this.statusFilter
    )
    .pipe(
      take(1),
      finalize(() => this.loadingService.hideLoading())
    )
    .subscribe({
      next: (resp: any) => {
        const { content, pageSize, totalPages, totalElements, pageNumber } = resp;
        this.dataSource.data = content;
        this.pageSize = pageSize;
        this.totalPages = totalPages;
        this.totalElements = totalElements;
        this.pageIndex = pageNumber;
      },
      error: (erro: any) => {
        const msg = erro?.error?.message || 'Erro ao carregar perfis';
        this.toastService.showError(msg);
      }
    });
  }

  // === EVENTOS DA TABELA (OUTPUTS) ===

  onSortChange(sortProperty: string): void {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection = this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }
    this.onGetUserProfiles(0);
  }

  pageChangeEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageSizeService.setPageSize(event.pageSize);
    this.onGetUserProfiles(event.pageIndex);
  }

  applyFilter(filterValue: string) {
    this.filterTextValue = filterValue;
    this.onGetUserProfiles(0);
  }

  filterStatusChanged(event: any) {
    this.statusFilter = event[0]?.property || '';
    this.onGetUserProfiles(0);
  }

  // === AÇÕES E NAVEGAÇÃO ===

  create() {
    this.router.navigate(['/perfil-usuario/novo']);
  }

  redirectToEditPage(id: any) {
    this.pageStateService.setPageState(this.router.url, this.pageIndex);
    this.router.navigate([`/perfil-usuario/${id}/editar`]);
  }

  // === CONFIRMAÇÕES (ATIVAR/INATIVAR/EXCLUIR) ===

  openDeleteDialog(id: any): void {
    this.openConfirmDialog('Excluir', 'Você tem certeza que deseja excluir este perfil?', () => {
      this.profileService.deleteProfile(id).subscribe({
        next: () => { this.toastService.showSuccess('Exclusão realizada com sucesso.'); this.onGetUserProfiles(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  openInactivateDialog(id: any): void {
    this.openConfirmDialog('Inativar', 'Você tem certeza que deseja inativar este perfil?', () => {
      this.profileService.inactivate(id).subscribe({
        next: () => { this.toastService.showSuccess('Perfil inativado com sucesso.'); this.onGetUserProfiles(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  openActivateDialog(id: any): void {
    this.openConfirmDialog('Ativar', 'Você tem certeza que deseja ativar este perfil?', () => {
      this.profileService.activate(id).subscribe({
        next: () => { this.toastService.showSuccess('Perfil ativado com sucesso.'); this.onGetUserProfiles(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  private openConfirmDialog(title: string, content: string, action: Function) {
    this.dialog.open(ModalComponent, {
      data: { modalTitle: title, modalContent: content, onPerformAction: action },
    });
  }

  // === CONTROLE DE ACESSO ===

  loadUserInfo() {
    const userId = this.authService.getUserIdFromToken();
    if (userId) {
      this.userService.getMenuUsuario(userId).subscribe((response: any) => {
        response.profileUser.permissaoPerfis.forEach((permission: any) => {
          this.checkFunctionality(permission.funcionalidade);
        });
      });
    }
  }

  checkFunctionality(functionality: any) {
    const normalize = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    if (normalize(functionality.nome) === normalize(this.currentRoute) || functionality.url === this.currentRoute) {
      this.handleRestrictions(functionality.restricoes);
    }
    if (functionality.subFuncionalidades?.length > 0) {
      functionality.subFuncionalidades.forEach((sub: any) => this.checkFunctionality(sub));
    }
  }

  handleRestrictions(restrictions: any[]) {
    restrictions.forEach(r => {
      if (r.nome === 'Inclusão') this.buttonInclusaoDisabled = true;
      if (r.nome === 'Exclusão') this.buttonExclusaoDisabled = true;
      if (r.nome === 'Edição') this.buttonEdicaoDisabled = true;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}